export type PlatformAdministratorAccountStatus =
  'PENDING_INVITATION' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

export type PlatformAdministratorMembershipStatus =
  'PENDING_INVITATION' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

export type PlatformAdministratorOrganizationStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

export type PlatformAdministratorInvitationStatus = 'PENDING' | 'EXPIRED' | 'CONSUMED' | 'REVOKED';

export interface PlatformAdministratorInvitationResult {
  readonly id: string;
  readonly status: PlatformAdministratorInvitationStatus;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface PlatformAdministratorResult {
  readonly membershipId: string;
  readonly userId: string;
  readonly givenName: string;
  readonly familyName: string;
  readonly email: string;
  readonly accountStatus: PlatformAdministratorAccountStatus;
  readonly membershipStatus: PlatformAdministratorMembershipStatus;
  readonly organization: {
    readonly id: string;
    readonly displayName: string;
    readonly status: PlatformAdministratorOrganizationStatus;
  };
  readonly latestInvitation: PlatformAdministratorInvitationResult | null;
}

export type PlatformAccountRole = 'ORGANIZATION_ADMIN' | 'PROVIDER' | 'CLIENT';

export interface PlatformAccountResult {
  readonly membershipId: string | null;
  readonly userId: string;
  readonly financeDemoEnabled?: boolean;
  readonly givenName: string;
  readonly familyName: string;
  readonly email: string;
  readonly role: PlatformAccountRole | null;
  readonly accountStatus: PlatformAdministratorAccountStatus;
  readonly membershipStatus: PlatformAdministratorMembershipStatus | null;
  readonly createdAt: string;
  readonly lastActivityAt: string | null;
  readonly organization: {
    readonly id: string;
    readonly displayName: string;
    readonly status: PlatformAdministratorOrganizationStatus;
  } | null;
  readonly latestInvitation: PlatformAdministratorInvitationResult | null;
}

export interface PlatformAccountInterventionResult {
  readonly accountStatus: PlatformAdministratorAccountStatus;
  readonly membershipStatus: PlatformAdministratorMembershipStatus;
  readonly revokedSessions: number;
}

export interface PlatformUnattachedAccountArchiveResult {
  readonly accountStatus: 'ARCHIVED';
  readonly membershipStatus: null;
  readonly revokedSessions: number;
}

export interface PlatformDashboardMetricsResult {
  readonly totalOrganizations: number;
  readonly totalAccounts: number;
  readonly totalAdministrators: number;
  readonly activeAccounts: number;
  readonly inactiveAccounts: number;
  readonly pendingInvitations: number;
  readonly accountsCreatedLast30Days: number;
  readonly projectCount: number | null;
  readonly monitoredServerCount: number | null;
}

export interface PlatformDashboardResult {
  readonly metrics: PlatformDashboardMetricsResult;
  readonly recentAdministrators: readonly PlatformAccountResult[];
  readonly mostActiveAdministrators: readonly PlatformAccountResult[];
  readonly leastRecentlyActiveAccounts: readonly PlatformAccountResult[];
}

export type PlatformOrganizationFormType =
  'COMPANY' | 'ASSOCIATION' | 'PUBLIC_INSTITUTION' | 'INDIVIDUAL';

export type PlatformOrganizationPersistenceType =
  | 'INDIVIDUAL'
  | 'FRENCH_COMPANY'
  | 'ASSOCIATION'
  | 'FOREIGN_COMPANY'
  | 'PUBLIC_INSTITUTION'
  | 'OTHER';

export interface PlatformOrganizationResult {
  readonly id: string;
  readonly displayName: string;
  readonly type: PlatformOrganizationPersistenceType;
  readonly status: PlatformAdministratorOrganizationStatus;
  readonly legalIdentifier: string | null;
  readonly logoUrl: string | null;
  readonly addressLine1: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly countryCode: string | null;
  readonly websiteUrl: string | null;
}

export interface PlatformOrganizationCreateInput {
  readonly displayName: string;
  readonly formType: PlatformOrganizationFormType;
  readonly legalIdentifier: string | null;
  readonly logoUrl: string | null;
  readonly addressLine1: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  readonly websiteUrl: string | null;
}

export interface PlatformAdministratorCandidateResult {
  readonly userId: string;
  readonly givenName: string;
  readonly familyName: string;
  readonly email: string;
  readonly accountStatus: PlatformAdministratorAccountStatus;
  readonly eligible: boolean;
  readonly ineligibilityReason: 'ALREADY_ATTACHED' | 'ACCOUNT_UNAVAILABLE' | null;
  readonly membership: {
    readonly id: string;
    readonly role: PlatformAccountRole;
    readonly status: PlatformAdministratorMembershipStatus;
    readonly organization: {
      readonly id: string;
      readonly displayName: string;
    };
  } | null;
}

export type PlatformAdministratorProvisioningInput =
  | {
      readonly mode: 'NEW';
      readonly email: string;
      readonly givenName: string;
      readonly familyName: string;
    }
  | {
      readonly mode: 'EXISTING';
      readonly userId: string;
    };

export interface PlatformAdministratorProvisioningResult {
  readonly organizationId: string;
  readonly membershipId: string;
  readonly userId: string;
  readonly accountStatus: PlatformAdministratorAccountStatus;
  readonly membershipStatus: PlatformAdministratorMembershipStatus;
  readonly shouldIssueInvitation: boolean;
  readonly invitationDelivery: 'NOT_REQUIRED' | 'SENT' | 'FAILED';
  readonly invitationExpiresAt: string | null;
}

export class PlatformApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'PlatformApiError';
  }
}

async function requestJson<T>(
  method: 'GET' | 'POST' | 'DELETE',
  path: string,
  options: { readonly csrfToken?: string; readonly body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.csrfToken) headers['X-CSRF-Token'] = options.csrfToken;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const request: RequestInit = {
    method,
    credentials: 'same-origin',
    ...(Object.keys(headers).length > 0 ? { headers } : {}),
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  };
  const response = await fetch(`/api${path}`, request);

  if (!response.ok) {
    throw new PlatformApiError(
      'La demande de supervision ne peut pas être traitée.',
      response.status,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function queryPath(path: string, query: string): string {
  const normalized = query.trim();
  return normalized.length === 0 ? path : `${path}?q=${encodeURIComponent(normalized)}`;
}

export const platformApi = {
  getDashboard(): Promise<PlatformDashboardResult> {
    return requestJson('GET', '/platform/dashboard');
  },

  listAccounts(): Promise<readonly PlatformAccountResult[]> {
    return requestJson('GET', '/platform/accounts');
  },

  setFinanceDemo(
    userId: string,
    enabled: boolean,
    csrfToken: string,
  ): Promise<{ enabled: boolean }> {
    return requestJson('POST', `/platform/accounts/${encodeURIComponent(userId)}/finance-demo`, {
      csrfToken,
      body: { enabled },
    });
  },

  checkAccountInterventionReadiness(): Promise<{ readonly ready: true }> {
    return requestJson('GET', '/platform/account-interventions/readiness');
  },

  suspendAccount(
    membershipId: string,
    reason: string,
    csrfToken: string,
  ): Promise<PlatformAccountInterventionResult> {
    return requestJson('POST', `/platform/accounts/${encodeURIComponent(membershipId)}/suspend`, {
      csrfToken,
      body: { reason },
    });
  },

  reactivateAccount(
    membershipId: string,
    csrfToken: string,
  ): Promise<PlatformAccountInterventionResult> {
    return requestJson(
      'POST',
      `/platform/accounts/${encodeURIComponent(membershipId)}/reactivate`,
      { csrfToken },
    );
  },

  archiveAccount(
    membershipId: string,
    reason: string,
    csrfToken: string,
  ): Promise<PlatformAccountInterventionResult> {
    return requestJson('POST', `/platform/accounts/${encodeURIComponent(membershipId)}/archive`, {
      csrfToken,
      body: { reason },
    });
  },

  archiveUnattachedAccount(
    userId: string,
    reason: string,
    csrfToken: string,
  ): Promise<PlatformUnattachedAccountArchiveResult> {
    return requestJson(
      'POST',
      `/platform/accounts/unattached/${encodeURIComponent(userId)}/archive`,
      { csrfToken, body: { reason } },
    );
  },

  listAdministrators(): Promise<readonly PlatformAdministratorResult[]> {
    return requestJson('GET', '/platform/administrators');
  },

  detachAdministrator(
    membershipId: string,
    reason: string,
    csrfToken: string,
  ): Promise<PlatformAccountInterventionResult> {
    return requestJson(
      'POST',
      `/platform/administrators/${encodeURIComponent(membershipId)}/detach`,
      { csrfToken, body: { reason } },
    );
  },

  reissueAdministratorInvitation(
    membershipId: string,
    csrfToken: string,
  ): Promise<{ readonly expiresAt: string }> {
    return requestJson(
      'POST',
      `/platform/administrators/${encodeURIComponent(membershipId)}/invitation`,
      { csrfToken },
    );
  },

  searchOrganizations(query: string): Promise<readonly PlatformOrganizationResult[]> {
    return requestJson('GET', queryPath('/platform/organizations', query));
  },

  searchAdministratorCandidates(
    query: string,
  ): Promise<readonly PlatformAdministratorCandidateResult[]> {
    return requestJson('GET', queryPath('/platform/administrator-candidates', query));
  },

  createOrganization(
    input: PlatformOrganizationCreateInput,
    csrfToken: string,
  ): Promise<PlatformOrganizationResult> {
    return requestJson('POST', '/platform/organizations', {
      csrfToken,
      body: input,
    });
  },

  addAdministrator(
    organizationId: string,
    input: PlatformAdministratorProvisioningInput,
    csrfToken: string,
  ): Promise<PlatformAdministratorProvisioningResult> {
    return requestJson(
      'POST',
      `/platform/organizations/${encodeURIComponent(organizationId)}/administrators`,
      { csrfToken, body: input },
    );
  },

  purgeAllSessions(csrfToken: string): Promise<{ readonly revokedSessions: number }> {
    return requestJson('DELETE', '/platform/sessions', { csrfToken });
  },
};
